/**
 * Lead Time Breakdown Parser
 * Handles files with dynamic workflow columns between Committed Date and Closed Date
 */

import { readFile } from '../shared/fileReader';
import { excelDateToJSDate } from '../../dateUtils';
import { 
  validateHeaders as validateBreakdownHeaders, 
  generateHeaderError as generateBreakdownHeaderError, 
  generateValidationError as generateBreakdownValidationError, 
  validateData as validateBreakdownData 
} from '../../validations/leadTimeBreakdown/validator';

/**
 * Helper function to parse date values (Excel serial numbers or date strings)
 * @param {any} dateValue - Value to parse as date
 * @returns {Date|null} Parsed date or null if invalid
 */
function parseDate(dateValue) {
  if (!dateValue) return null;
  
  // If it's already a Date object
  if (dateValue instanceof Date) {
    return dateValue;
  }
  
  // If it's a number (Excel serial date)
  if (typeof dateValue === 'number') {
    return excelDateToJSDate(dateValue);
  }
  
  // If it's a string, try to parse
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  
  return null;
}

/**
 * Processes a single lead time breakdown item
 * @param {Object} item - Raw item data
 * @param {Array} workflowColumns - Dynamic workflow columns
 * @returns {Object} Processed item with workflow dates
 */
function processLeadTimeBreakdownItem(item, workflowColumns) {
  // Parse dates
  const closedDate = parseDate(item['Closed Date']);
  
  // Extract workflow step dates
  const workflowDates = {};
  for (const column of workflowColumns) {
    const dateValue = item[column];
    
    if (dateValue) {
      const parsedDate = parseDate(dateValue);
      if (isValidDate(parsedDate)) {
        workflowDates[column] = parsedDate;
      }
    }
  }

  // Create processed row
  return {
    ID: item.ID || item['ID'],
    'Tipo de Item': item['Tipo de Item'],
    'Closed Date': closedDate,
    workflowDates,
    workflowColumns
  };
}

/**
 * Helper function to validate if a value is a valid date
 * @param {any} date - Value to check
 * @returns {boolean} True if valid date
 */
function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Parse Lead Time Breakdown file (CSV or XLSX)
 * @param {File} file - The file to parse
 * @returns {Promise<Array>} Parsed and validated data
 */
export async function parseLeadTimeBreakdownFile(file) {
  try {
    // Read file content
    const { headers, rawData } = await readFile(file);
    
    // Validate headers structure
    const headerValidation = validateBreakdownHeaders(headers);
    if (headerValidation.error) {
      throw new Error(generateBreakdownHeaderError(headerValidation.error));
    }
    
    const { workflowColumns } = headerValidation;
    
    
    // Validate data
    const { validationErrors, validData } = validateBreakdownData(rawData, workflowColumns);
    if (validationErrors.length > 0) {
      throw new Error(generateBreakdownValidationError(validationErrors));
    }
    
    // Process valid data
    const processedData = validData.map(item => processLeadTimeBreakdownItem(item, workflowColumns));
    
    
    return processedData;
    
  } catch (error) {
    console.error('Erro no parser de Lead Time Breakdown:', error);
    throw error;
  }
}
